import { Dispatch, SetStateAction, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArticleDraftRequest, ArticleRequest } from '../../api/queries';
import { useDebounce } from '../useDebounce';
import { useCreateArticleWithDraftMutation } from '../mutations/useCreateArticleWithDraftMutation';
import { useUpdateDraftArticleMutation } from '../mutations/useUpdateDraftArticleMutation';
import { ArticleUtils } from '../../utils/ArticleUtils/ArticleUtils';

type UseUpdateDraftArticleProps = {
  mode: 'create' | 'edit';
  guide: ArticleRequest;
  setGuide: Dispatch<SetStateAction<ArticleDraftRequest>>;
  id?: string;
  setTempUpdatedAt?: (d: Date) => void;
};

export function useUpdateDraftArticle({
  mode,
  guide,
  setGuide,
  id,
  setTempUpdatedAt,
}: UseUpdateDraftArticleProps) {
  const navigate = useNavigate();
  const { debounce } = useDebounce({ debounceTimeInMs: 2000 });

  const { mutate: updateDraftArticleMutation, isPending: isUpdatePending } =
    useUpdateDraftArticleMutation();

  const { mutate: createArticleWithDraft, isPending: isCreateArticlePending } =
    useCreateArticleWithDraftMutation();

  const updateDraftArticle = useCallback(
    <K extends keyof ArticleRequest>(
      key: K,
      value: ArticleRequest[K],
      debounceTimeInMs?: number,
    ) => {
      setGuide((prev) => ({ ...prev, [key]: value }));

      if (mode === 'create') {
        debounce(
          () => {
            createArticleWithDraft(
              {
                article: {
                  ...guide,
                  jsonContent: ArticleUtils.initialArticle,
                  updatedAt: new Date(),
                  [key]: value,
                },
              },
              {
                onSuccess: (res) =>
                  navigate(`/content-system/articles/edit/${res.draft.id}`),
              },
            );
          },
          { debounceTimeInMs: 800 },
        );
      }

      if (mode === 'edit') {
        const { draftId, ...guideWithoutDraftId } = guide;
        debounce(
          () => {
            updateDraftArticleMutation(
              {
                draftId: Number(id),
                draftArticle: {
                  ...guideWithoutDraftId,
                  updatedAt: new Date(),
                  [key]: value,
                },
              },
              {
                onSuccess: () => {
                  setTempUpdatedAt?.(new Date());
                },
              },
            );
          },
          { debounceTimeInMs },
        );
      }
    },
    [
      mode,
      guide,
      id,
      debounce,
      setGuide,
      updateDraftArticleMutation,
      createArticleWithDraft,
      navigate,
      setTempUpdatedAt,
    ],
  );

  return {
    updateDraftArticle,
    isUpdatePending,
    isCreateArticlePending,
  };
}
